import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Container, InnerContainer } from './styled'

import gsap from 'gsap'
import { MobileFooterInner } from '@/layouts/styled'
import { TextNavigationLink } from '@/components/TextNavigationLink'
import { navigate } from 'gatsby'
import FormStep from './FormStep'

interface Props {
  endpoint: string
  variant?: string
  useTimeSelectStep?: boolean
  withFooter: boolean
  backUrl?: string
  forwardsUrl?: string
  callToAction: string[]
  noWeekend: boolean
}

const Form: React.FC<Props> = ({
  variant,
  endpoint,
  useTimeSelectStep,
  withFooter,
  backUrl,
  forwardsUrl,
  callToAction,
  noWeekend,
}) => {
  const titles = [
    'pick a date',
    'choose a time',
    'your details',
    'request sent',
  ]
  /* eslint-disable no-useless-escape */
  const EmailExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  const PhoneExp = /^[0-9 \(\)]+$/
  const countryCodeExp = /^[0-9 \+\(\)]+$/
  const [step, setStep] = useState(1)
  const now = dayjs()

  const [state, setState] = useState({
    errors: {
      email: false,
      name: false,
      lastname: false,
      phone: false,
      pronoun: false,
      countryCode: false,
      agree: false,
    },
  })
  // apointment data
  const [day, setDay] = useState(now.date())
  const [month, setMonth] = useState(now.month())

  const [time, setTime] = useState('08:30')

  // move to details form
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')

  const [pronoun, setPronoun] = useState('')
  const [countryCode, setCountryCode] = useState('')

  const [phone, setPhone] = useState('')
  const [agree, setAgree] = useState('')
  const sendData = () => {
    const xhr = new XMLHttpRequest()
    const url = endpoint
    const data = {
      pronoun,
      name,
      lastName,
      email,
      countryCode,
      phone,
      agree,
      day,
      month: month + 1,
      time,
      submittedAt: Date.now(),
    }
    const JSONdata = JSON.stringify(data)
    xhr.open('POST', url)
    xhr.setRequestHeader('Content-Type', 'application/json')
    // xhr.onreadystatechange = () => {
    //   if (xhr.readyState === 4 && xhr.status === 200) {
    //     // Return a 200 response if the submission is successful
    //     console.info(xhr.responseText)
    //   } else if (xhr.readyState === 4 && xhr.status === 400) {
    //     // Return a 400 error the submission is rejected
    //     console.info(xhr.responseText)
    //   } else if (xhr.readyState === 4 && xhr.status === 403) {
    //     // Return a 403 error if the post isn't allowed
    //     console.info(xhr.responseText)
    //   } else if (xhr.readyState === 4 && xhr.status === 404) {
    //     // Return a 404 error
    //     console.info(xhr.responseText)
    //   }
    // }
    xhr.send(JSONdata)
  }

  const onGetErrors = () => {
    const { errors } = state
    errors.name = !name
    errors.lastname = !lastName
    errors.email = !email || !EmailExp.test(email)
    errors.countryCode = !countryCode || !countryCodeExp.test(countryCode)
    errors.phone = !phone || !PhoneExp.test(phone)
    errors.agree = !agree
    setState({
      ...state,
      errors,
    })
  }
  const hasError = () => {
    onGetErrors()
    const hasErrors = Object.keys(state.errors).filter((errorKey: string) => {
      const errorField = state.errors[errorKey]
      return errorField
    })

    return hasErrors?.length > 0
  }

  const submit = () => {
    if (!hasError()) {
      sendData()
    }
  }

  const getFormStateComplete = () => {
    if (step === 1 || (step === 2 && useTimeSelectStep)) {
      return true
    }
    if (
      ((step === 3 && useTimeSelectStep) ||
        (step === 2 && !useTimeSelectStep)) &&
      !hasError()
    ) {
      submit()
      return true
    }
    if (step === 4 || (step === 3 && !useTimeSelectStep)) {
      navigate(forwardsUrl)
    }
    return false
  }
  const getCorrectBackAction = () => {
    if (step === 1) {
      navigate(backUrl)
    } else {
      setStep((stepToUpdate) => stepToUpdate - 1)
    }
  }
  const onHandleStepChange = (direction) => {
    if (direction === 'up') {
      if (getFormStateComplete()) {
        setStep((stepToUpdate) => stepToUpdate + 1)
      }
    } else {
      getCorrectBackAction()
    }
  }

  const isServer = typeof window === 'undefined'
  let isMobile = false
  if (!isServer) {
    if (document.documentElement.clientWidth <= 960) {
      isMobile = true
    }
  }

  const {
    errors: {
      email: emailError,
      name: nameError,
      lastname: lastnameError,
      phone: phoneError,
      pronoun: pronounError,
      agree: agreeError,
      countryCode: countryCodeError,
    },
  } = state

  const [shouldTransition, setShouldTransition] = React.useState(false)
  const linkRef = useRef()
  const containerRef = useRef()
  const tl = useRef(null)

  const transitionIn = () => {
    if (!shouldTransition || !month) {
      return
    }
    setShouldTransition(false)
    if (step === 1 && tl.current) {
      tl.current = gsap.timeline({ repeat: 0 })
      tl.current.from(
        containerRef.current,
        0.5,
        { scaleY: 0, ease: 'circ.out' },
        0,
      )

      tl.current.fromTo(
        linkRef.current,
        0.5,
        { y: 10, opacity: 0 },
        { y: 0, opacity: 1, ease: 'circ.out' },
        0.2,
      )
    }
  }

  useEffect(() => setShouldTransition(true), [])
  useEffect(transitionIn, [month, shouldTransition])

  return (
    <Container variant={variant} data-url ref={containerRef}>
      <InnerContainer>
        {/* Select date */}
        {step === 1 && (
          <FormStep
            variant={variant}
            noWeekend={noWeekend}
            formData={{
              iconVariant: variant,
              title: titles[0],
              transitionType: 'transitionIn',
              noLine: false,
              formStepName: 'planner1',
              input: [
                {
                  value: month,
                  setValue: (value) => {
                    setMonth(value)
                  },
                },
                { value: day, setValue: (value) => setDay(value) },
              ],
            }}
          />
        )}
        {step === 2 && useTimeSelectStep && (
          <FormStep
            variant={variant}
            otherData={[month, day]}
            formData={{
              iconVariant: variant,
              title: titles[1],
              transitionType: 'transitionIn',
              noLine: false,
              formStepName: 'planner2',
              input: [{ value: time, setValue: (value) => setTime(value) }],
            }}
          />
        )}

        {((step === 3 && useTimeSelectStep) ||
          (step === 2 && !useTimeSelectStep)) && (
          <FormStep
            variant={variant}
            formData={{
              iconVariant: `${variant}Details`,
              title: titles[2],
              transitionType: 'transitionIn',
              noLine: false,
              formStepName: 'details',
              input: [
                { value: pronoun, setValue: (value) => setPronoun(value) },
                {
                  value: name,
                  setValue: (value) => setName(value),
                  error: nameError,
                },
                {
                  value: lastName,
                  setValue: (value) => setLastName(value),
                  error: lastnameError,
                },
                {
                  value: email,
                  setValue: (value) => setEmail(value),
                  error: emailError,
                },
                {
                  value: countryCode,
                  setValue: (value) => setCountryCode(value),
                  error: countryCodeError,
                },
                {
                  value: phone,
                  setValue: (value) => setPhone(value),
                  error: phoneError,
                },
                {
                  value: agree,
                  setValue: (value) => setAgree(value),
                  error: agreeError,
                },
              ],
            }}
          />
        )}
        {((step === 3 && !useTimeSelectStep) ||
          (step === 4 && useTimeSelectStep)) && (
          <FormStep
            variant={variant}
            formData={{
              formStepName: 'confirm',
              iconVariant: variant,
              title: titles[3],
              transitionType: 'transitionIn',
              noLine: false,
            }}
          />
        )}
      </InnerContainer>
      {withFooter && (
        <MobileFooterInner>
          {(step !== 1 && !isMobile) || isMobile ? (
            <TextNavigationLink
              data-url
              linkType="button"
              variant={variant}
              withBar={false}
              delay={-1}
              time={1}
              url=""
              text="back"
              buttonCallback={() => {
                onHandleStepChange('down')
              }}
            />
          ) : (
            <div />
          )}
          <TextNavigationLink
            data-url
            linkType="button"
            variant={variant}
            delay={1}
            time={1}
            url=""
            text={callToAction[step - 1]}
            buttonCallback={() => {
              onHandleStepChange('up')
            }}
          />
        </MobileFooterInner>
      )}
    </Container>
  )
}

Form.defaultProps = {
  variant: 'light',
  useTimeSelectStep: false,
  backUrl: null,
  forwardsUrl: null,
  noWeekend: false,
}

Form.propTypes = {
  variant: PropTypes.oneOf(['dark', 'light']),
  endpoint: PropTypes.string.isRequired,
  useTimeSelectStep: PropTypes.bool,
  backUrl: PropTypes.string,
  forwardsUrl: PropTypes.string,
  callToAction: PropTypes.array.isRequired,
  noWeekend: PropTypes.bool,
}

export default Form
